import { useCallback, useEffect, useMemo, useState } from "react";
import { actualizar, crear, eliminar, rpc } from "../../services/datos";
import { botonPeligro, botonPequeno, botonPrimario, botonSecundario, campo, etiqueta } from "../../shared/estilos";
import { confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";

const fechaLarga = (fecha) =>
  fecha ? new Date(`${fecha}T00:00:00`).toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" }) : "";

const nombre = (persona) => `${persona?.nombres || ""} ${persona?.apellidos || ""}`.trim() || "Sin nombre";

export function esDomingo(fecha) {
  return Boolean(fecha) && new Date(`${fecha}T00:00:00`).getDay() === 0;
}

// Mismo criterio que el backend: "profesor" o "maestro/a" en el rol es profesor; lo demás, asistente.
export const esProfesor = (rol) => /profesor|maestr/i.test(rol || "");

// El área de Mi Casa Kids la marca Configuración (es_infantil); sin esa marca, por su nombre.
const esAreaKids = (area) => area?.es_infantil ?? /infantil|kids/i.test(area?.nombre || "");

// Busca por nombre o correo entre quienes aún no están en el equipo (máximo 8 resultados).
export function buscarPersonas(miembros, texto, excluir = new Set()) {
  const limpio = texto.trim().toLowerCase();
  if (limpio.length < 2) return [];
  return miembros
    .filter((m) => !excluir.has(m.id))
    .filter((m) => `${m.nombres || ""} ${m.apellidos || ""} ${m.email || ""}`.toLowerCase().includes(limpio))
    .slice(0, 8);
}

const ESTADO = (t) => (t.rechazado ? ["No puede", "text-red-300"] : t.confirmado ? ["Confirmó", "text-green-300"] : ["Sin responder", "text-yellow-300"]);

// Pestaña Mi Casa Kids de Servidores: el equipo con su rol, quién sirve cada domingo
// (a mano o con la rotación automática) y el interruptor para automatizarla.
function PanelProfesores({ servicios, turnos, areas = [], miembros = [], miembrosArea = [], onCambio }) {
  const hoy = new Date().toLocaleDateString("en-CA"); // AAAA-MM-DD en la zona horaria local
  const domingos = useMemo(
    () => servicios.filter((s) => s.fecha >= hoy && esDomingo(s.fecha)).sort((a, b) => a.fecha.localeCompare(b.fecha)),
    [servicios, hoy]
  );
  const areaKids = areas.find(esAreaKids);
  const automatica = Boolean(areaKids?.rotacion_automatica);
  const equipo = miembrosArea.filter((v) => areaKids && v.area_id === areaKids.id && v.activo !== false);
  const profesores = equipo.filter((v) => esProfesor(v.rol_en_area));
  const asistentes = equipo.filter((v) => !esProfesor(v.rol_en_area));

  const [servicioId, setServicioId] = useState("");
  const [propuesta, setPropuesta] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [elegido, setElegido] = useState(null);
  const [rolNuevo, setRolNuevo] = useState("profesor");
  const [nuevoDomingo, setNuevoDomingo] = useState({ miembroId: "", rol: "Profesor" });

  useEffect(() => {
    if (!servicioId && domingos.length) setServicioId(String(domingos[0].id));
  }, [domingos, servicioId]);

  const cargarPropuesta = useCallback(async () => {
    setPropuesta(null);
    if (!servicioId || !automatica) return;
    try {
      setPropuesta(await rpc("rotacion_infantil", { p_servicio: Number(servicioId) }));
    } catch (error) {
      setMensaje(error.message);
    }
  }, [servicioId, automatica]);

  useEffect(() => { cargarPropuesta(); }, [cargarPropuesta, turnos]);

  const ejecutar = async (accion) => {
    setOcupado(true);
    setMensaje("");
    try {
      setMensaje(await accion());
      onCambio();
    } catch (error) {
      setMensaje(error.message || "No se pudo completar la acción");
    } finally {
      setOcupado(false);
    }
  };

  // ─── Interruptor de la rotación automática ───
  const cambiarAutomatica = async () => {
    const activar = !automatica;
    const pregunta = activar
      ? "¿Activar la rotación automática de Mi Casa Kids? Los domingos que ya tienen profesor y asistente se conservan; "
        + "los que estén vacíos se llenarán solos siguiendo el orden de quién sirvió más recientemente."
      : "¿Desactivar la rotación automática? Los turnos ya asignados se conservan y los próximos domingos los asignarás a mano.";
    if (!(await confirmar(pregunta, { peligro: false }))) return;
    ejecutar(async () => {
      await actualizar("areas_servicio", areaKids.id, { rotacion_automatica: activar });
      if (!activar) return "Rotación automática desactivada. Asigna cada domingo a mano.";
      const r = await rpc("generar_reuniones");
      return `Rotación automática activada.${r.rotaciones?.length ? ` Se llenaron ${r.rotaciones.length} reunión(es) que estaban vacías.` : ""}`;
    });
  };

  // ─── Equipo ───
  const cambiarRol = (vinculo, rol) => ejecutar(async () => {
    await actualizar("miembro_area", vinculo.id, { rol_en_area: rol });
    return `${nombre(vinculo.miembros)} ahora es ${rol}.`;
  });

  const agregar = () => ejecutar(async () => {
    await crear("miembro_area", { miembro_id: elegido.id, area_id: areaKids.id, rol_en_area: rolNuevo, activo: true });
    const texto = `${nombre(elegido)} se agregó al equipo como ${rolNuevo}.`;
    setElegido(null);
    setBusqueda("");
    return texto;
  });

  // ─── Quién sirve el domingo elegido (a mano) ───
  const turnosDelDomingo = turnos.filter((t) => t.servicio_id === Number(servicioId) && areaKids && t.area_id === areaKids.id);
  const enEsteDomingo = new Set(turnosDelDomingo.map((t) => t.miembro_id));

  // Al elegir a alguien se propone su rol del equipo; se puede cambiar (p. ej. dos profesores).
  const elegirParaDomingo = (miembroId) => {
    const vinculo = equipo.find((v) => String(v.miembro_id) === miembroId);
    setNuevoDomingo({ miembroId, rol: vinculo && !esProfesor(vinculo.rol_en_area) ? "Asistente" : "Profesor" });
  };

  const agregarAlDomingo = () => ejecutar(async () => {
    await crear("turnos_servicio", {
      servicio_id: Number(servicioId), area_id: areaKids.id, miembro_id: Number(nuevoDomingo.miembroId),
      rol_en_turno: nuevoDomingo.rol,
    });
    setNuevoDomingo({ miembroId: "", rol: "Profesor" });
    return `${nuevoDomingo.rol} agregado. Recibirá el aviso 7 días antes y un recordatorio el día anterior.`;
  });

  const quitarDelDomingo = async (turno) => {
    if (!(await confirmar(`¿Quitar a ${nombre(turno.miembros)} de este domingo? Si ya sabía de su turno, se le avisará.`, { aceptar: "Quitar" }))) return;
    ejecutar(async () => {
      await eliminar("turnos_servicio", turno.id);
      return `Se quitó a ${nombre(turno.miembros)} de este domingo.`;
    });
  };

  const asignarAutomatico = () => ejecutar(async () => {
    const resultado = await rpc("rotacion_infantil", { p_servicio: Number(servicioId), p_asignar: true });
    return resultado.creados.length ? `Se asignaron ${resultado.creados.length} persona(s) según la rotación.` : "Esta clase ya estaba completa.";
  });

  const rehacer = async () => {
    if (!(await confirmar("¿Rehacer la rotación de los próximos domingos con los roles actuales? "
      + "Se conservan los turnos confirmados y los que pusiste a mano; solo se avisa a quienes ya sabían de su turno y salen de una clase.", { aceptar: "Rehacer", peligro: false }))) return;
    ejecutar(async () => {
      const r = await rpc("rehacer_rotacion_infantil");
      return r.cambios ? `Rotación rehecha en ${r.clases} domingo(s): ${r.cambios} cambio(s).` : "La rotación ya estaba al día.";
    });
  };

  if (!areaKids) {
    return <p className="text-gray-400">Crea un área llamada “Infantil” en Configuración → Áreas de servicio para usar Mi Casa Kids.</p>;
  }

  const resultados = buscarPersonas(miembros, busqueda, new Set(equipo.map((v) => v.miembro_id)));

  return (
    <div className="space-y-6">
      <section className="panel flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-xl font-black uppercase tracking-tight text-white">Rotación automática</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-400">
            {automatica
              ? "Activada: cada domingo se asignan solos un profesor y un asistente, siguiendo el orden de quién sirvió más recientemente."
              : "Desactivada: eliges a mano quién sirve cada domingo. Cuando la actives, continuará desde el orden que hayas llevado."}
          </p>
        </div>
        <button onClick={cambiarAutomatica} disabled={ocupado} role="switch" aria-checked={automatica}
          className={`flex shrink-0 items-center gap-3 rounded-xl border px-4 py-2 text-sm font-bold transition ${automatica ? "border-white bg-white text-black" : "border-white/30 text-gray-300 hover:border-white"}`}>
          <span className={`h-3 w-3 rounded-full ${automatica ? "bg-black" : "bg-gray-600"}`} />
          {automatica ? "Activada" : "Desactivada"}
        </button>
      </section>

      {mensaje && <p className="rounded border border-white/15 bg-white/5 p-3 text-sm text-gray-200">{mensaje}</p>}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="panel space-y-4 p-5">
          <div>
            <h3 className="text-xl font-black uppercase tracking-tight text-white">Quién sirve cada domingo</h3>
          </div>
          <Selector aria-label="Domingo" value={servicioId} onChange={(e) => setServicioId(e.target.value)} className={campo}>
            {domingos.length === 0 && <option value="">No hay domingos programados</option>}
            {domingos.map((s) => <option key={s.id} value={s.id}>{fechaLarga(s.fecha)}</option>)}
          </Selector>

          <div>
            <p className={etiqueta}>Equipo de este domingo</p>
            {turnosDelDomingo.length === 0 ? (
              <p className="text-gray-400 text-sm">Aún no hay nadie asignado a este domingo.</p>
            ) : (
              <ul className="space-y-2">
                {turnosDelDomingo.map((t) => (
                  <li key={t.id} className="panel-interior flex items-center justify-between gap-3 p-3">
                    <span className="text-sm">
                      <span className="text-white font-semibold">{nombre(t.miembros)}</span>
                      <span className="text-gray-400"> · {t.rol_en_turno || "Servidor"}</span>
                    </span>
                    <span className="flex items-center gap-3">
                      <span className={`text-xs font-semibold ${ESTADO(t)[1]}`}>{ESTADO(t)[0]}</span>
                      <button onClick={() => quitarDelDomingo(t)} disabled={ocupado} className={botonPeligro}>Quitar</button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label htmlFor="kids-domingo-persona" className={etiqueta}>Agregar a este domingo</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Selector id="kids-domingo-persona" value={nuevoDomingo.miembroId} disabled={ocupado || !servicioId}
                onChange={(e) => elegirParaDomingo(e.target.value)} className={`${campo} flex-1`}>
                <option value="">Elegir persona del equipo...</option>
                {[["Profesores", profesores], ["Asistentes", asistentes]].map(([grupo, lista]) => (
                  <optgroup key={grupo} label={grupo}>
                    {lista.filter((v) => !enEsteDomingo.has(v.miembro_id)).map((v) => (
                      <option key={v.id} value={v.miembro_id}>{nombre(v.miembros || miembros.find((m) => m.id === v.miembro_id))}</option>
                    ))}
                  </optgroup>
                ))}
              </Selector>
              <Selector aria-label="Rol en este domingo" value={nuevoDomingo.rol} disabled={ocupado}
                onChange={(e) => setNuevoDomingo({ ...nuevoDomingo, rol: e.target.value })} className={`${campo} sm:w-40`}>
                <option value="Profesor">Profesor</option>
                <option value="Asistente">Asistente</option>
              </Selector>
              <button onClick={agregarAlDomingo} disabled={ocupado || !nuevoDomingo.miembroId} className={botonPrimario}>Agregar</button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Normalmente es un profesor y un asistente. Si ningún asistente puede, agrega un segundo profesor: con dos personas el domingo queda completo y la rotación no lo cambia.
            </p>
          </div>

          {automatica && (
            <div className="space-y-3 border-t border-gray-800 pt-4">
              {propuesta && !propuesta.ya_asignado && (propuesta.profesor || propuesta.asistentes.length > 0) && (
                <p className="text-sm text-gray-400">
                  Según la rotación corresponde: {[propuesta.profesor && `${nombre(propuesta.profesor)} (profesor)`,
                    ...propuesta.asistentes.map((a) => `${nombre(a)} (asistente)`)].filter(Boolean).join(" y ")}.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <button onClick={asignarAutomatico} disabled={ocupado || !servicioId || propuesta?.ya_asignado} className={botonPrimario}>
                  {propuesta?.ya_asignado ? "Domingo completo" : "Completar con la rotación"}
                </button>
                <button onClick={rehacer} disabled={ocupado} className={botonSecundario}>Rehacer rotación</button>
              </div>
              <p className="text-xs text-gray-500">“Rehacer rotación” recalcula los próximos domingos después de cambiar roles o agregar personas; conserva los confirmados.</p>
            </div>
          )}
        </section>

        <section className="panel space-y-4 p-5">
          <div>
            <h3 className="text-xl font-black uppercase tracking-tight text-white">Profesores y asistentes</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[["Profesores", profesores.length], ["Asistentes", asistentes.length]].map(([texto, valor]) => (
              <div key={texto} className="panel-interior p-4">
                <p className="text-3xl font-bold text-white">{valor}</p>
                <p className="text-gray-400 text-xs">{texto}</p>
              </div>
            ))}
          </div>

          <ul className="space-y-2">
            {equipo.map((v) => (
              <li key={v.id} className="panel-interior flex items-center justify-between gap-3 p-3">
                <span className="text-sm font-semibold text-white">{nombre(v.miembros || miembros.find((m) => m.id === v.miembro_id))}</span>
                <Selector aria-label={`Rol de ${nombre(v.miembros)}`} value={esProfesor(v.rol_en_area) ? "profesor" : "asistente"}
                  onChange={(e) => cambiarRol(v, e.target.value)} disabled={ocupado} className={`${campo} w-36 py-2`}>
                  <option value="profesor">Profesor</option>
                  <option value="asistente">Asistente</option>
                </Selector>
              </li>
            ))}
            {equipo.length === 0 && <li className="text-sm text-gray-400">Aún no hay nadie en el equipo.</li>}
          </ul>

          <div className="border-t border-gray-800 pt-4">
            <label htmlFor="kids-buscar" className={etiqueta}>Agregar al equipo</label>
            {elegido ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <span className="flex-1 rounded border border-white/40 bg-black p-3 text-sm font-semibold text-white">
                  {nombre(elegido)} <button onClick={() => setElegido(null)} className="ml-2 text-xs text-gray-400 underline">cambiar</button>
                </span>
                <Selector aria-label="Rol de la persona nueva" value={rolNuevo} onChange={(e) => setRolNuevo(e.target.value)} className={`${campo} sm:w-36`}>
                  <option value="profesor">Profesor</option>
                  <option value="asistente">Asistente</option>
                </Selector>
                <button onClick={agregar} disabled={ocupado} className={botonPrimario}>Agregar</button>
              </div>
            ) : (
              <>
                <input id="kids-buscar" type="search" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Escribe un nombre o un correo" className={campo} autoComplete="off" />
                {busqueda.trim().length >= 2 && (
                  <ul className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-gray-700">
                    {resultados.length === 0 ? (
                      <li className="p-3 text-sm text-gray-400">No hay personas con ese nombre fuera del equipo.</li>
                    ) : resultados.map((m) => (
                      <li key={m.id}>
                        <button onClick={() => setElegido(m)} className="w-full border-b border-gray-800 p-3 text-left last:border-b-0 hover:bg-white/5">
                          <span className="block text-sm font-semibold text-white">{nombre(m)}</span>
                          {m.email && <span className="block text-xs text-gray-500">{m.email}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <p className="text-xs text-gray-500">
        Las reuniones (domingos 10:00, miércoles 19:00 y martes de oración 5:00) se crean solas para este mes y el siguiente.
        <button onClick={() => ejecutar(async () => {
          const r = await rpc("generar_reuniones");
          return `${r.creados.length} reunión(es) nueva(s).`;
        })} disabled={ocupado} className={`${botonPequeno} ml-3`}>Completar reuniones ahora</button>
      </p>
    </div>
  );
}

export default PanelProfesores;
